import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, makeStyles, Theme, Typography } from '@material-ui/core';
import { IValidationError, onlinePatientManagement } from '@ngt/online-patient-management-ui';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { FormRenderProps, useFormState } from 'react-final-form';
import { Link } from 'react-router-dom';
import { InvalidSaeForm as InvalidSae } from '../../api/Neon.Dtos';
import { useSaeById } from '../../store/modules/saereview/sae';
import AsyncForm from '../FinalForm/AsyncForm';

import * as Dtos from '../../api/Neon.Dtos';

import arrayMutators from 'final-form-arrays';
import { RequestState, IRequestState } from '@ngt/request-utilities';
import OpmsField from '../OpmsForm/OpmsField';
import OpmsFieldGroup from '../OpmsForm/OpmsFieldGroup';
import OpmsInput, { pascalToCamelCase } from '../OpmsForm/OpmsInput';
import DatePicker from '../OpmsForm/Fields/DatePicker';
import Select from '../OpmsForm/Fields/Select';
import TextField from '../OpmsForm/Fields/TextField';
import OpmsFormGrid, { IOpmsFormGridCell } from '../OpmsForm/Grids/Form/OpmsFormGrid';
import FileUpload from '../OpmsForm/Fields/FileUpload';
import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';


interface IInvalidSaeFormProps {
    patientStudyNumber?: string,
    saeNumber?: number;
    readOnly?: boolean;
}

interface IInvalidSaeFormInternalProps extends FormRenderProps<Dtos.InvalidSaeForm>  {
    invalidSaeForm?: Dtos.InvalidSaeForm | null,
    sae?: Dtos.Sae<Dtos.SaeForm, Dtos.Patient> | null,
    readOnly?: boolean;
    confirmationDialogOpen?: boolean;
    toggleConfirmationDialog: () => void;
}

const useInvalidSaeForm = (sae: Dtos.Sae<Dtos.SaeForm, Dtos.Patient>|null, saeLoadState: IRequestState<Dtos.ResponseStatus>) => {
    const [form, setForm] = React.useState<InvalidSae | null | undefined>(null);
    const [formLoading, setFormLoading] = React.useState(true);
    const [formError, setFormError] = React.useState(false);

    React.useEffect(() => {
        if (!sae?.invalidForm) {
            setForm(new Dtos.InvalidSaeForm({
                patientStudyNumber: sae?.patient?.studyNumber,
                saeNumber: sae?.form?.saeNumber
            }));
        } else {
            setForm(sae.invalidForm)
        }
    }, [sae, setForm]);

    React.useEffect(() => {
        if (saeLoadState?.state === RequestState.None || saeLoadState?.state === RequestState.Pending) {
            setFormLoading(true);
        }

        if (saeLoadState?.state === RequestState.Failure) {
            setFormError(true);
            setFormLoading(false);
        }

        if (saeLoadState?.state === RequestState.Success) {
            setFormError(false);
            setFormLoading(false);
        }
    }, [saeLoadState, setFormLoading, setFormError]);

    return {
        form,
        formLoading,
        formError
    }
}

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    loading: {
        padding: theme.spacing(5),
        width: '100%',
        display: 'flex',
        justifyContent: 'space-around'
    },
    fieldContainer: {
        marginTop: theme.spacing(3)
    },
    subHeading: {
        padding: theme.spacing(3, 3, 1, 2)
    },
    buttonSet: {
        textAlign: 'right',
        padding: theme.spacing(3)
    },
    button: {
        marginLeft: theme.spacing(2),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    }
}));

const InvalidSaeForm: React.FunctionComponent<IInvalidSaeFormProps> = ({
    saeNumber,
    patientStudyNumber,
    readOnly
}) => {
    const classes = useStyles();

    const [sae, saeLoadState, saeActions,] = useSaeById(
        patientStudyNumber!,
        +saeNumber!
    );

    const { form, formLoading, formError } = useInvalidSaeForm(sae, saeLoadState);

    const formSubscriptionParams = {
        submitting: true,
        pristine: false,
        submitError: true,
        dirtySinceLastSubmit: true,
        hasValidationErrors: true,
        valid: true,
        validating: true,
        errors: true,
        form: true
    };

    const onValidate = React.useCallback(async (values: Dtos.InvalidSaeForm) => {
        // Send request to server.
        const response = await onlinePatientManagement.client
            .post(new Dtos.ValidateInvalidSaeForm({ invalidSaeForm: new Dtos.InvalidSaeForm({ ...values }) }));

        // parse errors into a format the form understands.
        const groupErrors = response.validationResult?.errors?.reduce((a: Record<string, Dtos.SaeFormValidationError[]>, b: Dtos.SaeFormValidationError) => {
            const propertyName = b.property?.includes(".") ? b.property : pascalToCamelCase(b.property)

            if (!a[propertyName]) {
                a[propertyName] = [];
            }
            
            a[propertyName].push(b);

            return a;
        }, {}) ?? {};

        return groupErrors as any as IValidationError[];

    }, [])

    const { enqueueSnackbar } = useSnackbar();

    const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);

    const toggleConfirmationDialog = React.useCallback(() => {
        setConfirmationDialogOpen(!confirmationDialogOpen);
    }, [confirmationDialogOpen, setConfirmationDialogOpen]);

    const onSubmit = React.useCallback(async (form: Dtos.InvalidSaeForm) => {
        try {
            setConfirmationDialogOpen(false);

            // Send request to server.
            const response = await onlinePatientManagement.client
                .post(new Dtos.SaveInvalidSaeForm({ invalidSaeForm: new Dtos.InvalidSaeForm({ ...form }) }));

            if (response) {
                enqueueSnackbar(`Invalid SAE form was succcessfully saved.`, { variant: 'success' });

                saeActions.load();
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to save the form: ${errorMessage}`, { variant: 'error' });
        }
    }, [enqueueSnackbar, saeActions, setConfirmationDialogOpen]);

    const invalidSaeForm = React.useCallback((props: FormRenderProps<Dtos.InvalidSaeForm>) => {
        return (
            <InvalidSaeFormInternal
                invalidSaeForm={form}
                sae={sae}
                confirmationDialogOpen={confirmationDialogOpen}
                toggleConfirmationDialog={toggleConfirmationDialog}
                readOnly={readOnly}
                {...props}
            />
        );
    }, [form, sae, confirmationDialogOpen, toggleConfirmationDialog]);

    if (formLoading || formError) {
        return <div className={classes.loading}>
            <CircularProgress />
        </div>
    }

    return (
        <>
            <AsyncForm
                initialValues={form}
                onValidate={onValidate}
                onSubmit={onSubmit}
                formSubscription={formSubscriptionParams}
                validationDebounceTimeout={200}
                component={invalidSaeForm}
                mutators={{
                    ...arrayMutators
                }}
                
            />
        </>
    );
};

const InvalidSaeFormInternal: React.FunctionComponent<IInvalidSaeFormInternalProps> = ({
    invalidSaeForm,
    sae,
    readOnly,
    confirmationDialogOpen,
    toggleConfirmationDialog,
    ...formRenderProps
}) => {
    const classes = useStyles();

    const [updateInvalidSaeFiles, setUpdateInvalidSaeFiles] = React.useState<boolean>(false);

    const { values: formStateValues } = useFormState({ subscription: { values: true } });

    const [[canAdministerOpms]] = useBctApiPermissions([Dtos.Permission.OpmsAdminister]);

    const handleRedactFinish = React.useCallback(() => {
        setUpdateInvalidSaeFiles(true);
    }, [setUpdateInvalidSaeFiles]);

    React.useEffect(() => {
        const hasGuidFile = formStateValues?.files?.some((x: Dtos.InvalidSaeFile) => !!x.guid) ?? false;
        
        if (updateInvalidSaeFiles && hasGuidFile) {
            formRenderProps.handleSubmit();
            setUpdateInvalidSaeFiles(false);
        }
    }, [updateInvalidSaeFiles, formStateValues]);

    const formDisabled = React.useMemo(() => {
        return readOnly === true || formRenderProps.submitting;
    }, [readOnly, formRenderProps.submitting]);

    const formReason = formRenderProps.form.getFieldState('reason')?.value ?? 0;

    const columns = React.useMemo(() => {
        return [
            {
                name: 'Name',
                header: (
                    <Typography
                        style={{ fontWeight: 'bold' }}
                    >
                        Name
                    </Typography>
                ),
                content: (
                    <OpmsInput
                        component={TextField}
                        disabled={formDisabled}
                    />
                )
            },
            {
                name: 'Guid',
                header: (
                    <Typography
                        style={{ fontWeight: 'bold' }}
                    >
                        Upload
                    </Typography>
                ),
                content: (
                    <OpmsInput
                        component={FileUpload}
                        existsFieldName="Exists"
                        fileIdFieldName="Id"
                        imageFieldName="Image"
                        disabled={formDisabled}
                        viewLink="/api/sae/invalid-sae-file/view"
                        downloadLink="/api/sae/invalid-sae-file/download"
                        onRedactFinish={handleRedactFinish}
                        allowRedactOnFormDisable={canAdministerOpms ?? false} 
                    />
                ),
                condition: (params) => {
                    return invalidSaeForm?.id == null || (params?.index ?? 0) > 0
                }
            }
        ] as IOpmsFormGridCell[];
    }, [formDisabled, invalidSaeForm, handleRedactFinish]);

    return (
        <div className={classes.fieldContainer}>
            <OpmsField
                name="dateDeterminedInvalid"
                label={<Typography>Date determined invalid</Typography>}
                xl={5}
                lg={5}
                md={6}
                sm={12}
                xs={12}
            >
                <OpmsInput
                    component={DatePicker}
                    disabled={formDisabled}
                />
            </OpmsField>
            <OpmsFieldGroup>
                <OpmsField
                    name="reason"
                    label={<Typography>Reason</Typography>}
                    xl={5}
                    lg={5}
                    md={6}
                    sm={12}
                    xs={12}
                >
                    <OpmsInput
                        component={Select}
                        lookup={sae?.lookups?.find(x => x.propertyName === "Reason")}
                        disabled={formDisabled}
                    />
                </OpmsField>
                {
                    formReason === Dtos.InvalidSaeReason.Other && <OpmsField
                        name="reasonSpecify"
                        label={<Typography>Specify</Typography>}
                        xl={5}
                        lg={5}
                        md={6}
                        sm={12}
                        xs={12}
                    >
                        <OpmsInput
                            component={TextField}
                            disabled={formDisabled}
                        />
                    </OpmsField>
                }
            </OpmsFieldGroup>
            <OpmsField
                name="decidedById"
                label={<Typography>Decided by</Typography>}
                xl={5}
                lg={5}
                md={6}
                sm={12}
                xs={12}
            >
                <OpmsInput
                    component={Select}
                    lookup={sae?.lookups?.find(x => x.propertyName === "DecidedById")}
                    disabled={formDisabled}
                />
            </OpmsField>
            <OpmsField
                name="comments"
                label={<Typography>Narrative Summary</Typography>}
                xl={5}
                lg={5}
                md={6}
                sm={12}
                xs={12}
            >
                <OpmsInput
                    component={TextField}
                    multiline={true}
                    rows={5}
                    variant="outlined"
                    disabled={formDisabled}
                />
            </OpmsField>

            <OpmsFieldGroup>
                <Typography
                    variant="h2"
                    color="primary"
                    className={classes.subHeading}
                >
                    Files
                </Typography>
                <OpmsFormGrid
                    name="Files"
                    columns={columns}
                    allowAdd={!formDisabled}
                    allowRemove={!formDisabled}
                    hideRemove={(index) => invalidSaeForm?.id != null && index === 0}
                />
            </OpmsFieldGroup>
            <div
                className={classes.buttonSet}
            >
                {
                    !formDisabled && !!invalidSaeForm?.id && (
                        <>
                            <Button
                                disabled={formRenderProps.validating || !formRenderProps.valid}
                                variant="contained"
                                color="primary"
                                onClick={formRenderProps.handleSubmit}
                                className={classes.button}
                            >
                                Submit Form
                            </Button>
                            <Button
                                disabled={formRenderProps.validating || !formRenderProps.valid}
                                variant="contained"
                                color="primary"
                                onClick={formRenderProps.handleSubmit}
                                className={classes.button}
                            >
                                Submit Form And Return
                            </Button>
                        </>
                    )
                }

                {
                    !formDisabled && !invalidSaeForm?.id && (
                        <>
                            <Button
                                disabled={formRenderProps.validating || !formRenderProps.valid}
                                variant="contained"
                                color="primary"
                                onClick={toggleConfirmationDialog}
                                className={classes.button}
                            >
                                Mark as Invalid
                            </Button>
                        </>
                    )
                }

                <Button
                    disabled={formRenderProps.submitting}
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={
                        !!sae && sae?.status !== Dtos.SaeStatus.Invalid ?
                            `/sae/${sae.patient.institutionCode}/${sae.patient.studyNumber}/${sae.form.saeNumber}` :
                            `/sae`
                    }
                    className={classes.button}
                >
                    {
                        formDisabled ?
                            'Back' :
                            'Cancel'
                    }
                </Button>
            </div>

            <InvalidSaeConfirmationDialog
                open={confirmationDialogOpen === true}
                toggleConfirmationDialog={toggleConfirmationDialog}
                sae={sae}
                onSubmit={async () => {
                    await formRenderProps.handleSubmit();
                }}
            />
        </div>
    );
}

const InvalidSaeConfirmationDialog: React.FunctionComponent<{
    open: boolean,
    toggleConfirmationDialog: () => void,
    sae: Dtos.Sae<Dtos.SaeForm, Dtos.Patient> | null | undefined
    onSubmit: () => Promise<any> | undefined
}> = ({
    open,
    toggleConfirmationDialog,
    sae, 
    onSubmit
}) => {
        return (
            <Dialog open={open} onClose={toggleConfirmationDialog} aria-labelledby="remove-dialog" maxWidth="xs">
                <DialogTitle>
                    Mark SAE as invalid
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Typography color={"error"}>The SAE data will be <strong>PERMANENTLY DELETED</strong>. This <strong>CANNOT</strong> be undone.</Typography>
                        <br />
                        Are you sure you want to mark patient {sae?.patient.studyNumber}'s SAE #{sae?.form?.saeNumber} as invalid?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        onClick={toggleConfirmationDialog}
                    >
                        No
                    </Button>
                    <Button
                        color="primary"
                        onClick={onSubmit}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

export default InvalidSaeForm;